import { useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Alert, Box, Stack } from '@mui/material'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { OrgTree, Organization } from '@/types/organizations'
import {
  addSelectionProperties,
  createOrganizationMap,
  getFilteredOrgsNodesData,
  getNodesToExpandForMultipleOrgs,
} from '@/utils/organization'
import OrganizationsSearchInput from '@/components/organizations/OrganizationsSearchInput'
import OrganizationTreeItemWithCheckbox from '@/components/organizations-tree/OrganizationTreeItemWithCheckbox'

const SEARCH_DEBOUNCE_MS = 250

// Past this the tree scrolls inside its own pane. Without it a large org tree
// makes the whole dialog taller than the window, and scrolling the dialog drags
// the search box and title out of view while the dropdown stays behind.
// The dialog's own chrome — margins, title, footer, padding — runs about 220px,
// so this leaves headroom on anything down to roughly an 800px-tall window.
const MAX_TREE_HEIGHT_RATIO = 0.7

type OrganizationTreeWithImplicitInheritanceSearchProps = {
  organizations: Organization[]
  rootTreeOrgId: string
  selectedOrgsIds: string[]
  checkboxIsDisabled?: boolean
  label?: string
  helperText?: string
  showSearchInput?: boolean
  onDeselectOrg?: (orgId: string) => void
  onSelectOrg?: (orgId: string) => void
  setSelectedOrgs?: (orgs: Organization[]) => void
}

export default function OrganizationTreeWithImplicitInheritanceSearch({
  organizations,
  rootTreeOrgId,
  selectedOrgsIds,
  onDeselectOrg,
  onSelectOrg,
  setSelectedOrgs,
  checkboxIsDisabled,
  helperText,
  label,
  showSearchInput = true,
}: OrganizationTreeWithImplicitInheritanceSearchProps) {
  // The input keeps the raw value so typing stays instant; the tree filters off
  // the debounced copy, so a burst of keystrokes costs one filter pass.
  const [inputValue, setInputValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const debouncedSetSearchTerm = useMemo(
    () => debounce(setSearchTerm, SEARCH_DEBOUNCE_MS),
    [],
  )

  useEffect(() => {
    debouncedSetSearchTerm(inputValue)
  }, [debouncedSetSearchTerm, inputValue])

  // A pending timer firing after unmount would set state on a dead component.
  useEffect(
    () => () => debouncedSetSearchTerm.cancel(),
    [debouncedSetSearchTerm],
  )

  const selectedOrgs = useMemo(
    () => organizations.filter(({ id }) => selectedOrgsIds.includes(id)),
    [selectedOrgsIds, organizations],
  )

  const organizationMap = useMemo(
    () => createOrganizationMap(organizations),
    [organizations],
  )

  const defaultExpanded = useMemo(
    () => getNodesToExpandForMultipleOrgs(selectedOrgs, organizationMap),
    [selectedOrgs, organizationMap],
  )

  const [expanded, setExpanded] = useState(defaultExpanded)

  const { visibleOrgIds, nodesToExpand } = useMemo(
    () =>
      getFilteredOrgsNodesData({
        organizations,
        organizationMap,
        defaultExpanded,
        searchedValue: searchTerm,
      }),
    [defaultExpanded, searchTerm, organizationMap, organizations],
  )

  // Walks and mutates every node, so it must not run on renders that changed
  // neither the tree nor the selection — typing is the common one.
  const { orgTree = {}, implicitlySelectedIds = [] } = useMemo(
    () =>
      addSelectionProperties(organizationMap[rootTreeOrgId], selectedOrgsIds),
    [organizationMap, rootTreeOrgId, selectedOrgsIds],
  )

  useEffect(() => {
    if (searchTerm || !expanded.length) {
      setExpanded(nodesToExpand)
    }
  }, [expanded.length, searchTerm, nodesToExpand])

  // Filtering shrinks the tree, which resizes whatever is holding this — the
  // dialog jumps under the user mid-search. Remember how tall the tree is while
  // they aren't searching and hold that as a floor. Measuring an inner element
  // and applying the floor to its parent keeps the floor out of its own input.
  const treeRef = useRef<HTMLDivElement>(null)
  const [heightFloor, setHeightFloor] = useState<number>()
  const isSearching = !!searchTerm

  useEffect(() => {
    const element = treeRef.current
    if (!element || isSearching) return

    const observer = new ResizeObserver(() => {
      const measured = element.getBoundingClientRect().height
      // CSS resolves min-height over max-height, so the cap has to be applied
      // to the stored floor rather than left to the style alone.
      const cap = window.innerHeight * MAX_TREE_HEIGHT_RATIO
      setHeightFloor(Math.min(measured, cap) || undefined)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [isSearching])

  return (
    <Stack spacing={2} direction="row">
      {showSearchInput && (
        <Box sx={{ width: 300, flexShrink: 0 }}>
          <OrganizationsSearchInput
            label={label}
            helperText={helperText}
            inputValue={inputValue}
            setInputValue={setInputValue}
            selected={selectedOrgs}
            implicitlySelectedIds={implicitlySelectedIds}
            setSelected={(orgs) => {
              setSelectedOrgs?.(orgs)
              setExpanded(
                getNodesToExpandForMultipleOrgs(orgs, organizationMap),
              )
            }}
            disableCloseOnSelect
            organizations={organizations}
          />
        </Box>
      )}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: heightFloor,
          maxHeight: `${MAX_TREE_HEIGHT_RATIO * 100}vh`,
          overflowY: 'auto',
        }}
      >
        <Box ref={treeRef}>
          {visibleOrgIds.size ? (
            <SimpleTreeView
              slots={{
                collapseIcon: ExpandMore,
                expandIcon: ChevronRight,
              }}
              expandedItems={
                !searchTerm && !expanded.length ? [rootTreeOrgId] : expanded
              }
              multiSelect
              selectedItems={selectedOrgsIds}
              onExpandedItemsChange={(e, ids) => {
                // When clicking on a checkbox, onNodeToggle gets triggered before the checkbox
                // onChange, so we prevent default expand/collapse behavior here.
                if ((e.target as HTMLElement).tagName !== 'INPUT') {
                  setExpanded(ids)
                }
              }}
            >
              <OrganizationTreeItemWithCheckbox
                checkboxIsDisabled={checkboxIsDisabled}
                orgTree={orgTree as OrgTree}
                searchTerm={searchTerm}
                getIsVisible={(id: string) => visibleOrgIds.has(id)}
                onChange={(checked, orgId) => {
                  if (checked) {
                    onSelectOrg?.(orgId)
                  } else {
                    onDeselectOrg?.(orgId)
                  }
                }}
              />
            </SimpleTreeView>
          ) : (
            <Alert severity="info">No results found.</Alert>
          )}
        </Box>
      </Box>
    </Stack>
  )
}
